import { Check } from 'lucide-react';
import { useLanguage, useLanguageSwitch } from '@infra/i18n';
import { brandColors, brandGradients } from '@shared/constants';
import { useLanguagePickerVisible } from './useLanguagePickerVisible';

/**
 * The language choices as plain in-place rows, for panels that are already a
 * popup: the mobile drawer and the TopBar overflow menu.
 *
 * Deliberately NOT the header's `LanguageToggle`. That one is a Radix dropdown
 * whose content is portaled to `document.body`, so it sits outside the panel
 * that contains its trigger — the panel's own outside-click handler then tears
 * the panel down on mousedown, taking the dropdown with it before the choice
 * registers. Rows avoid the nesting entirely, and a menu inside a menu is poor
 * on a phone anyway.
 */
export function LanguageOptions({ onPicked }: { onPicked?: () => void }) {
  const { language, availableLanguages } = useLanguage();
  const switchLang = useLanguageSwitch();
  const visible = useLanguagePickerVisible();

  if (!visible) return null;

  return (
    // Wrap, not one row: at 260px six equal buttons leave far too little for a
    // label plus the active check.
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {availableLanguages.map((lang) => {
        const active = lang.code === language;
        return (
          <button
            key={lang.code}
            type="button"
            onClick={() => {
              if (lang.code === language) return;
              onPicked?.();
              void switchLang(lang.code).catch((e) => console.error('Language switch failed:', e));
            }}
            style={{
              flex: '1 1 calc(50% - 4px)',
              minWidth: 64,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              height: 38,
              borderRadius: 12,
              fontSize: 12.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              cursor: 'pointer',
              border: active ? '1px solid transparent' : `1px solid ${brandColors.border}`,
              background: active ? brandGradients.brandPrimary : brandColors.surfaceCard,
              color: active ? brandColors.onDark : brandColors.textSecondary,
              transition: 'all 0.2s ease',
            }}
          >
            {lang.badge}
            {active && <Check className="h-3.5 w-3.5" />}
          </button>
        );
      })}
    </div>
  );
}
